import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Fingerprint, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  isPlatformPasskeyAvailable,
  registerPasskey,
  listPasskeys,
  wasEnrollmentPrompted,
  markEnrollmentPrompted,
  rememberPasskeyEmail,
} from '../lib/webauthn';

/**
 * One-time post-login prompt inviting the user to enable Face ID / Touch ID /
 * Windows Hello / Android fingerprint. Silently does nothing if:
 *  - the device doesn't have a platform authenticator
 *  - the user already has a passkey
 *  - the user has been prompted (accepted or dismissed) before
 */
export default function PasskeyEnrollmentPrompt({ user }) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (!user?.email || attempted.current) return;
    attempted.current = true;
    (async () => {
      if (wasEnrollmentPrompted(user.email)) return;
      const available = await isPlatformPasskeyAvailable();
      if (!available) return;
      try {
        const existing = await listPasskeys();
        if (existing.length > 0) {
          rememberPasskeyEmail(user.email);
          return;
        }
      } catch {
        return;
      }
      // Show after a short beat so the dashboard has time to settle
      setTimeout(() => setVisible(true), 800);
    })();
  }, [user?.email]);

  const enable = async () => {
    setBusy(true);
    try {
      await registerPasskey();
      rememberPasskeyEmail(user.email);
      markEnrollmentPrompted(user.email);
      toast.success('Biometric sign-in enabled. Next time, just look or tap.');
      setVisible(false);
    } catch (err) {
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'AbortError') {
        // user cancelled — do not show as an error
      } else {
        toast.error('Could not enable biometric sign-in. You can try later from Settings.');
      }
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    markEnrollmentPrompted(user?.email);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#36454F]/60 backdrop-blur-sm p-4 animate-fade-in"
      data-testid="passkey-enroll-modal"
    >
      <div className="w-full max-w-md bg-white border border-[#36454F]/15 rounded-sm shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-[#B76E79]/15 to-[#E0BFB8]/10 p-6 flex items-start justify-between border-b border-[#36454F]/10">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-white border border-[#B76E79]/25 flex items-center justify-center mr-4 shadow-sm">
              <Fingerprint className="w-6 h-6 text-[#B76E79]" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Sign in faster next time
              </h3>
              <p className="text-xs text-[#36454F]/55 font-['Manrope'] mt-0.5">Optional · takes 3 seconds</p>
            </div>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="text-[#36454F]/40 hover:text-[#36454F] transition-colors"
            data-testid="passkey-enroll-dismiss"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm text-[#36454F]/80 font-['Manrope'] leading-relaxed mb-4">
            Turn on <strong>Face ID, Touch ID, Windows Hello,</strong> or your device&apos;s fingerprint
            to sign in without typing your password. Your biometric data never leaves the device —
            we only store a public key linked to your account.
          </p>
          <p className="text-xs text-[#36454F]/55 font-['Manrope'] italic mb-6">
            &ldquo;The plans of the diligent lead to profit as surely as haste leads to poverty.&rdquo; — Proverbs 21:5
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={enable}
              disabled={busy}
              className="flex-1 h-11 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
              data-testid="passkey-enroll-enable-btn"
            >
              {busy ? 'Enabling…' : 'Enable biometric sign-in'}
            </Button>
            <Button
              onClick={dismiss}
              variant="ghost"
              className="h-11 text-[#36454F]/60 hover:text-[#36454F] hover:bg-transparent font-['Manrope']"
              data-testid="passkey-enroll-later-btn"
            >
              Maybe later
            </Button>
          </div>
          <p className="text-[11px] text-[#36454F]/45 font-['Manrope'] mt-4 text-center">
            You can always manage this later in <Link to="/settings" className="text-[#B76E79] hover:underline">Settings</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
